import { useState, useCallback, useMemo, useRef, useEffect, Fragment } from 'react';
import { useQueryParams } from '@/hooks/useQueryParams';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  ClipboardCheck,
  Check,
  X,
  Loader2,
  Clock,
  Banknote,
  ChevronDown,
  ChevronUp,
  Users,
  BadgeCheck,
  BadgeDollarSign,
  ChevronsDownUp,
  ChevronsUpDown,
  CheckCheck,
  XCircle,
  Building2,
  Download,
  Gift,
  Car,
  ExternalLink,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { useApproveReport, useRejectReport, useRePending, useBulkApprove, useBulkReject, useActiveEmploymentRoles } from '../hooks';
import type { PendingReport } from '../types';
import { localizedName } from '@/utils/localized';
import { useHospitals } from '@/features/hospitals';
import toast from 'react-hot-toast';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import {
  ADMIN_COLORS,
  staggerContainer,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { GlassCard } from '@/components/glass';
import { StatusBadge, STATUS_BADGE_CONFIG, STATUS_LABEL_KEY } from '../components/StatusBadge';
import { AnimatedValue } from '../components/AnimatedValue';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { ExportWizardModal } from '../components/ExportWizardModal';
import { FormModal, MODAL_COLORS } from '@/components/modal';
import { TimeRangePicker } from '../components/TimeRangePicker';
import { normalizeExportData } from '../utils/export/normalizeExportData';
import { exportCsv } from '../utils/export/csvExporter';
import type { ExportLayout } from '../utils/export/exportTypes';
import type { ExportColumnId, ExportGroupBy } from '../utils/export/exportColumns';
import type { TimeRange } from '../utils/timeRange';
import {
  createMonthlyRange,
  timeRangeToApiParams,
  isSingleMonth,
} from '../utils/timeRange';

const PAGE_SIZE = 200; // Load all pending for grouping

/** Derive supervision bonus status from contributing reports: lowest status wins */
function deriveSvStatus(reports?: Array<{ status: string }>): string {
  if (!reports || reports.length === 0) return 'SUBMITTED';
  const statuses = new Set(reports.map((r) => r.status));
  if (statuses.has('SUBMITTED')) return 'SUBMITTED';
  if (statuses.has('APPROVED')) return 'APPROVED';
  return 'PAID';
}


function GroupStatusBadge({ reports }: { reports: PendingReport[] }) {
  const { t } = useTranslation();
  if (reports.length === 0) return null;
  const nonRejected = reports.filter(r => r.status !== 'REJECTED');
  const hasRejected = reports.some(r => r.status === 'REJECTED');
  const hasPending = nonRejected.some(r => r.status === 'SUBMITTED');
  if (hasPending) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#fef9c3', color: '#a16207', boxShadow: '0 1px 4px rgba(161,98,7,0.2)' }}>
        <Clock className="w-3 h-3" /> {t('admin.statusPending')}
      </span>
    );
  }
  if (hasRejected && nonRejected.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#fee2e2', color: '#dc2626', boxShadow: '0 1px 4px rgba(220,38,38,0.2)' }}>
        <XCircle className="w-3 h-3" /> {t('admin.statusRejected')}
      </span>
    );
  }
  const allPaid = nonRejected.length > 0 && nonRejected.every(r => r.status === 'PAID');
  if (allPaid) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', color: '#78350f', boxShadow: '0 1px 5px rgba(120,53,15,0.25)' }}>
        <BadgeDollarSign className="w-3 h-3" /> {t('admin.statusPaid')}
      </span>
    );
  }
  // All approved (or mix of approved+paid, possibly some rejected)
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#dcfce7', color: '#15803d', boxShadow: '0 1px 4px rgba(21,128,61,0.2)' }}>
      <Check className="w-3 h-3" /> {t('admin.statusApproved')}
    </span>
  );
}

interface TherapistGroup {
  therapistId: string;
  therapistName: string;
  reports: PendingReport[];
  totalReports: number;
  totalQuantity: number;
  totalAmount: number;
  totalHospitals: number;
}

function employeeName(t: PendingReport['therapist']): string {
  if (t.firstName && t.lastName) return `${t.firstName} ${t.lastName}`;
  if (t.firstName) return t.firstName;
  return '';
}

export function ReportApprovalPage() {
  const { t, i18n } = useTranslation();
  const sym = useCurrencySymbol();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const { hasPermission } = usePermissions();

  const approve = useApproveReport();
  const reject = useRejectReport();
  const rePending = useRePending();
  const bulkApprove = useBulkApprove();
  const bulkReject = useBulkReject();

  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [svDetail, setSvDetail] = useState<{ sv: any; contrib: any; therapistName: string } | null>(null);
  const [isExportWizardOpen, setIsExportWizardOpen] = useState(false);
  const [wizardExporting, setWizardExporting] = useState(false);

  const currentMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const [params, setParams] = useQueryParams({
    month: { default: currentMonth },
    hospital: { default: '' },
    role: { default: '' },
    name: { default: '' },
    status: { default: '' },
    report: { default: '' },
  });

  const [timeRange, setTimeRange] = useState<TimeRange>(() => createMonthlyRange(params.month));

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    setParams({ month: range.startMonth });
  };

  const hospitalFilter = params.hospital;
  const roleFilter = params.role;
  const nameFilter = params.name;
  const statusFilter = params.status;

  const topBarRef = useRef<HTMLDivElement>(null);
  const [, setTopBarVisible] = useState(true);

  useEffect(() => {
    const el = topBarRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTopBarVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Filter data
  const { data: hospitalsData } = useHospitals();
  const hospitals = hospitalsData?.hospitals ?? [];
  const { data: employmentRoles } = useActiveEmploymentRoles();
  const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';

  // Load all pending reports
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['admin', 'pending-reports', 'infinite', statusFilter, timeRange.startMonth, timeRange.endMonth],
    queryFn: async ({ pageParam }) => {
      const { data } = await http.get<{ success: boolean; reports: PendingReport[]; allowancesByTherapist: Record<string, Array<{ id: string; name: string; nameEn: string | null; code: string; amount: number; period: string }>>; supervisionBonusesByTherapist: Record<string, { totalAmount: number; breakdown: Array<{ hospitalId: string; hospitalName: string; hospitalNameEn: string | null; hospitalCode: string; reportTypeCode: string; totalUnits: number; bonusPerUnit: number; amount: number }> }>; total: number; statusCounts: { SUBMITTED: number; APPROVED: number; REJECTED: number; PAID: number } }>(
        '/admin/pending-reports',
        { params: { page: pageParam, limit: PAGE_SIZE, ...(statusFilter && { status: statusFilter }), ...timeRangeToApiParams(timeRange) } },
      );
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.reports.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const allReports = useMemo(
    () => data?.pages.flatMap(p => p.reports) ?? [],
    [data],
  );

  const detailReport = useMemo(
    () => allReports.find(r => r.id === params.report) ?? null,
    [allReports, params.report],
  );

  const statusCounts = data?.pages[0]?.statusCounts ?? { SUBMITTED: 0, APPROVED: 0, REJECTED: 0, PAID: 0 };

  // Merge allowances from all pages
  const allowancesByTherapist = useMemo(() => {
    const merged: Record<string, Array<{ id: string; name: string; nameEn: string | null; code: string; amount: number; period: string }>> = {};
    for (const page of data?.pages ?? []) {
      for (const [tid, allowances] of Object.entries(page.allowancesByTherapist || {})) {
        if (!merged[tid]) merged[tid] = allowances;
      }
    }
    return merged;
  }, [data]);

  // Merge supervision bonuses from all pages
  const supervisionBonusesByTherapist = useMemo(() => {
    const merged: Record<string, { totalAmount: number; breakdown: Array<{ hospitalId: string; hospitalName: string; hospitalNameEn: string | null; hospitalCode: string; reportTypeCode: string; totalUnits: number; bonusPerUnit: number; amount: number; contributors?: Array<{ therapistId: string; name: string; units: number; reports?: Array<{ id: string; date: string; status: string; reportType: string }> }> }> }> = {};
    for (const page of data?.pages ?? []) {
      for (const [tid, sv] of Object.entries(page.supervisionBonusesByTherapist || {})) {
        if (!merged[tid]) merged[tid] = sv;
      }
    }
    return merged;
  }, [data]);

  // Group by therapist (with filters applied)
  const groups = useMemo(() => {
    const map = new Map<string, TherapistGroup>();
    for (const r of allReports) {
      // Apply hospital filter at the report level
      if (hospitalFilter && r.hospital?.id !== hospitalFilter) continue;
      // Apply role filter at the report level (employment role)
      if (roleFilter && r.therapist.employmentRoleCode !== roleFilter) continue;

      const tid = r.therapist.id;
      // Apply name filter at therapist level
      if (nameFilter) {
        const fullName = employeeName(r.therapist).toLowerCase();
        if (!fullName.includes(nameFilter.toLowerCase())) continue;
      }

      if (!map.has(tid)) {
        map.set(tid, {
          therapistId: tid,
          therapistName: employeeName(r.therapist),
          reports: [],
          totalReports: 0,
          totalQuantity: 0,
          totalAmount: 0,
          totalHospitals: 0,
        });
      }
      const g = map.get(tid)!;
      g.reports.push(r);
      if (r.status !== 'REJECTED') {
        g.totalReports++;
        g.totalQuantity += r.quantity || 1;
        g.totalAmount += r.calculatedAmount ?? 0;
      }
    }
    // Count unique hospitals per therapist
    for (const [, g] of map) {
      g.totalHospitals = new Set(g.reports.filter(r => r.hospital).map(r => r.hospital!.id)).size;
    }
    // Add allowance + supervision bonus totals (only for therapists that survived filtering)
    for (const [tid, g] of map) {
      const allowances = allowancesByTherapist[tid] || [];
      for (const a of allowances) {
        g.totalAmount += a.amount;
      }
      const svBonus = supervisionBonusesByTherapist[tid];
      if (svBonus) {
        g.totalAmount += svBonus.totalAmount;
      }
    }
    // Sort by total amount descending
    return [...map.values()].sort((a, b) => b.totalAmount - a.totalAmount);
  }, [allReports, allowancesByTherapist, supervisionBonusesByTherapist, hospitalFilter, roleFilter, nameFilter]);

  const totalPending = allReports.length;
  const totalEmployees = groups.length;
  const totalAmount = useMemo(
    () => groups.reduce((sum, g) => sum + g.totalAmount, 0),
    [groups],
  );

  const topHospital = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    for (const g of groups) {
      for (const r of g.reports) {
        if (r.status === 'REJECTED' || !r.hospital) continue;
        const key = r.hospital.id;
        const existing = counts.get(key);
        if (existing) {
          existing.count++;
        } else {
          counts.set(key, { name: localizedName(r.hospital, i18n.language), count: 1 });
        }
      }
    }
    let best: { name: string; count: number } | null = null;
    for (const v of counts.values()) {
      if (!best || v.count > best.count) best = v;
    }
    return best;
  }, [groups, i18n.language]);

  // Derive selected report objects for bulk operations
  const selectedReportObjects = useMemo(() => {
    const objs: { id: string; reportType: string }[] = [];
    for (const g of groups) {
      for (const r of g.reports) {
        if (selectedReports.has(r.id)) {
          objs.push({ id: r.id, reportType: r.reportType });
        }
      }
    }
    return objs;
  }, [groups, selectedReports]);

  const allReportIds = useMemo(
    () => groups.flatMap(g => g.reports.map(r => r.id)),
    [groups],
  );

  const isTherapistAllSelected = useCallback((therapistId: string) => {
    const group = groups.find(g => g.therapistId === therapistId);
    if (!group || group.reports.length === 0) return false;
    return group.reports.every(r => selectedReports.has(r.id));
  }, [groups, selectedReports]);

  const isTherapistIndeterminate = useCallback((therapistId: string) => {
    const group = groups.find(g => g.therapistId === therapistId);
    if (!group || group.reports.length === 0) return false;
    const count = group.reports.filter(r => selectedReports.has(r.id)).length;
    return count > 0 && count < group.reports.length;
  }, [groups, selectedReports]);

  const toggleReport = (reportId: string) => {
    triggerHapticFeedback();
    const next = new Set(selectedReports);
    if (next.has(reportId)) next.delete(reportId);
    else next.add(reportId);
    setSelectedReports(next);
  };

  const toggleTherapist = (therapistId: string) => {
    triggerHapticFeedback();
    const group = groups.find(g => g.therapistId === therapistId);
    if (!group) return;
    const next = new Set(selectedReports);
    const allSelected = group.reports.every(r => next.has(r.id));
    if (allSelected) {
      group.reports.forEach(r => next.delete(r.id));
    } else {
      group.reports.forEach(r => next.add(r.id));
    }
    setSelectedReports(next);
  };

  const toggleSelectAll = () => {
    triggerHapticFeedback();
    if (selectedReports.size === allReportIds.length && allReportIds.length > 0) {
      setSelectedReports(new Set());
    } else {
      setSelectedReports(new Set(allReportIds));
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (expandedIds.size === groups.length) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(groups.map(g => g.therapistId)));
    }
  };

  const handleApprove = async (report: PendingReport) => {
    triggerHapticFeedback();
    try {
      await approve.mutateAsync({ reportType: report.reportType, id: report.id });
      toast.success(t('admin.reportApproved'));
      if (params.report === report.id) setParams({ report: '' });
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleReject = async (report: PendingReport) => {
    if (!rejectReason.trim()) return;
    triggerHapticFeedback([50, 30, 50]);
    try {
      await reject.mutateAsync({ reportType: report.reportType, id: report.id, reason: rejectReason });
      setRejectingId(null);
      setRejectReason('');
      toast.success(t('admin.reportRejected'));
      if (params.report === report.id) setParams({ report: '' });
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleRePending = async (report: PendingReport) => {
    triggerHapticFeedback();
    try {
      await rePending.mutateAsync({ reportType: report.reportType, id: report.id });
      toast.success(t('admin.reportRePending'));
      if (params.report === report.id) setParams({ report: '' });
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedReportObjects.length === 0) return;
    triggerHapticFeedback([30, 20, 30]);
    try {
      const count = await bulkApprove.mutateAsync(selectedReportObjects);
      setSelectedReports(new Set());
      toast.success(`${count} ${t('admin.reportsApproved')}`);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleBulkReject = async () => {
    if (!bulkRejectReason.trim() || selectedReportObjects.length === 0) return;
    triggerHapticFeedback([50, 30, 50]);
    try {
      const rejected = await bulkReject.mutateAsync({ reportIds: selectedReportObjects, reason: bulkRejectReason });
      setSelectedReports(new Set());
      setBulkRejectOpen(false);
      setBulkRejectReason('');
      toast.success(`${rejected} ${t('admin.reportsRejected')}`);
    } catch {
      toast.error(t('common.error'));
    }
  };

  // Export helpers
  const exportFilename = `approvals-${isSingleMonth(timeRange) ? timeRange.startMonth : `${timeRange.startMonth}_${timeRange.endMonth}`}`;

  // Wizard export helpers
  const wizardEmployees = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; role: string }>();
    for (const g of groups) {
      if (!seen.has(g.therapistId)) {
        const firstReport = g.reports[0];
        const role = firstReport?.therapist.employmentRoleName ?? '';
        seen.set(g.therapistId, { id: g.therapistId, name: g.therapistName, role });
      }
    }
    return [...seen.values()];
  }, [groups]);

  const wizardStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const g of groups) {
      for (const r of g.reports) set.add(r.status);
    }
    return [...set];
  }, [groups]);

  const handleWizardExport = async (params: {
    employeeIds: Set<string>;
    statuses: Set<string>;
    reportTypes: Set<string>;
    aggregated: boolean;
    format: 'csv' | 'xlsx' | 'pdf';
    layout: ExportLayout;
    columns: ExportColumnId[];
    groupBy: ExportGroupBy;
  }) => {
    setWizardExporting(true);
    try {
      // Filter groups by selected employees
      const filteredGroups = groups.filter(g => params.employeeIds.has(g.therapistId));
      // Filter reports by selected statuses within each group
      const groupsWithFilteredReports = filteredGroups.map(g => ({
        ...g,
        reports: g.reports.filter(r => params.statuses.has(r.status)),
      })).filter(g => g.reports.length > 0);

      const payload = normalizeExportData({
        title: t('export.approvalReport'),
        groups: groupsWithFilteredReports.map(g => ({ ...g, employeeId: g.therapistId, employeeName: g.therapistName })),
        allowancesByEmployee: allowancesByTherapist,
        supervisionBonusesByEmployee: supervisionBonusesByTherapist,
        selectedIds: new Set<string>(),
        selectionMode: 'report',
        currencySymbol: sym,
        language: i18n.language,
        timeRange,
        filters: {
          status: statusFilter || undefined,
          hospitalName: hospitalFilter ? hospitals.find((h: any) => h.id === hospitalFilter)?.name : undefined,
          roleName: roleFilter ? employmentRoles?.find(r => r.code === roleFilter)?.name : undefined,
        },
        columns: params.columns,
        groupBy: params.groupBy,
      });

      if (params.format === 'csv') {
        await exportCsv(payload, exportFilename, params.layout);
      } else if (params.format === 'xlsx') {
        const { exportXlsx } = await import('../utils/export/xlsxExporter');
        await exportXlsx(payload, exportFilename, params.layout);
      } else {
        const { exportPdf } = await import('../utils/export/pdfExporter');
        await exportPdf(payload, exportFilename, params.layout);
      }
    } finally {
      setWizardExporting(false);
    }
  };

  const closeDetailModal = () => setParams({ report: '' });

  const detailCardStyle = { backgroundColor: MODAL_COLORS.interactive, border: `1px solid ${MODAL_COLORS.border}` };

  return (
    <motion.div
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.4 } } }}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: ADMIN_COLORS.primarySoft }}
        >
          <ClipboardCheck className="w-5 h-5" style={{ color: ADMIN_COLORS.primaryText }} />
        </div>
        <div>
          <h1
            className="text-lg sm:text-2xl font-semibold tracking-wide"
            style={{ color: ADMIN_COLORS.navy, letterSpacing: '0.05em' }}
          >
            {t('admin.pendingApprovals')}
          </h1>
        </div>
      </div>

      {/* Stats — Status counts */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-3xl glass-surface" style={{ borderInlineStart: '3px solid #a16207', border: '1px solid rgba(255,255,255,0.3)', borderInlineStartWidth: '3px', borderInlineStartColor: '#a16207' }}>
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-3.5 h-3.5 shrink-0" style={{ color: '#a16207' }} />
            <p className="text-[10px] font-bold uppercase" style={{ color: '#a16207' }}>
              {t('admin.statusPending')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={statusCounts.SUBMITTED} />
          </p>
        </div>
        <div className="p-3 rounded-3xl glass-surface" style={{ borderInlineStart: '3px solid #15803d', border: '1px solid rgba(255,255,255,0.3)', borderInlineStartWidth: '3px', borderInlineStartColor: '#15803d' }}>
          <div className="flex items-center gap-2 mb-1">
            <BadgeCheck className="w-3.5 h-3.5 shrink-0" style={{ color: '#15803d' }} />
            <p className="text-[10px] font-bold uppercase" style={{ color: '#15803d' }}>
              {t('admin.statusApproved')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={statusCounts.APPROVED} />
          </p>
        </div>
        <div className="p-3 rounded-3xl glass-surface" style={{ borderInlineStart: '3px solid #dc2626', border: '1px solid rgba(255,255,255,0.3)', borderInlineStartWidth: '3px', borderInlineStartColor: '#dc2626' }}>
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-3.5 h-3.5 shrink-0" style={{ color: '#dc2626' }} />
            <p className="text-[10px] font-bold uppercase" style={{ color: '#dc2626' }}>
              {t('admin.statusRejected')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={statusCounts.REJECTED} />
          </p>
        </div>
        <div className="p-3 rounded-3xl glass-surface" style={{ borderInlineStart: '3px solid #78350f', border: '1px solid rgba(255,255,255,0.3)', borderInlineStartWidth: '3px', borderInlineStartColor: '#78350f' }}>
          <div className="flex items-center gap-2 mb-1">
            <BadgeDollarSign className="w-3.5 h-3.5 shrink-0" style={{ color: '#78350f' }} />
            <p className="text-[10px] font-bold uppercase" style={{ color: '#78350f' }}>
              {t('admin.statusPaid')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={statusCounts.PAID} />
          </p>
        </div>
      </div>

      {/* Stats — Filtered totals */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.employees')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={totalEmployees} />
          </p>
        </div>
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.reportsCol')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={totalPending} />
          </p>
        </div>
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.topHospital')}
            </p>
          </div>
          <p className="text-sm font-bold text-slate-800 truncate">
            {topHospital ? `${topHospital.name} (${topHospital.count})` : '—'}
          </p>
        </div>
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <Banknote className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.totalAmount')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={Math.round(totalAmount)} currency />
          </p>
        </div>
      </div>

      {/* Filters */}
      <GlassCard title={t('common.filters')}>
      <div className="space-y-3">
        <TimeRangePicker value={timeRange} onChange={handleTimeRangeChange} compact />
        <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={nameFilter}
          onChange={(e) => setParams({ name: e.target.value })}
          placeholder={t('admin.filterByName')}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-w-[160px]"
        />

        <select
          value={statusFilter}
          onChange={(e) => setParams({ status: e.target.value })}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <option value="">{t('common.all')}</option>
          <option value="SUBMITTED">{t('admin.statusPending')}</option>
          <option value="APPROVED">{t('admin.statusApproved')}</option>
          <option value="REJECTED">{t('admin.statusRejected')}</option>
          <option value="PAID">{t('admin.statusPaid')}</option>
        </select>

        <select
          value={roleFilter}
          onChange={(e) => setParams({ role: e.target.value })}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <option value="">{t('admin.allRoles')}</option>
          {employmentRoles?.map((r) => (
            <option key={r.id} value={r.code}>
              {localizedName(r, i18n.language)}
            </option>
          ))}
        </select>

        <select
          value={hospitalFilter}
          onChange={(e) => setParams({ hospital: e.target.value })}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <option value="">{t('common.all')} {t('hospitals.title')}</option>
          {hospitals.map((h: any) => (
            <option key={h.id} value={h.id}>
              {localizedName(h, i18n.language)}{h.city ? ` (${i18n.language === 'en' && h.cityEn ? h.cityEn : h.city})` : ''}
            </option>
          ))}
        </select>
      </div>
      </div>
      </GlassCard>

      {/* Quick actions */}
      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={toggleExpandAll}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all active:translate-y-px"
            style={{
              backgroundColor: 'rgba(255,255,255,0.5)',
              border: `1px solid ${ADMIN_COLORS.border}`,
              color: ADMIN_COLORS.textSecondary,
            }}
          >
            {expandedIds.size === groups.length ? (
              <><ChevronsDownUp className="w-3.5 h-3.5" /> {t('common.collapseAll')}</>
            ) : (
              <><ChevronsUpDown className="w-3.5 h-3.5" /> {t('common.expandAll')}</>
            )}
          </button>
          <button
            onClick={toggleSelectAll}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all active:translate-y-px"
            style={{
              backgroundColor: 'rgba(255,255,255,0.5)',
              border: `1px solid ${ADMIN_COLORS.border}`,
              color: ADMIN_COLORS.textSecondary,
            }}
          >
            {selectedReports.size === allReportIds.length && allReportIds.length > 0 ? (
              <><XCircle className="w-3.5 h-3.5" /> {t('common.deselectAll')}</>
            ) : (
              <><CheckCheck className="w-3.5 h-3.5" /> {t('common.selectAll')}</>
            )}
          </button>
          {selectedReports.size > 0 && statusFilter !== 'PAID' && (hasPermission('action:approve_reports') || hasPermission('action:reject_reports')) && (
            <>
              {hasPermission('action:approve_reports') && (
                <button
                  onClick={handleBulkApprove}
                  disabled={bulkApprove.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition-all active:translate-y-px disabled:opacity-50"
                  style={{
                    backgroundColor: ADMIN_COLORS.success,
                    boxShadow: '0 1px 4px rgba(10,149,72,0.25)',
                  }}
                >
                  <Check className="w-3.5 h-3.5" />
                  {t('reports.approve')} ({selectedReportObjects.length})
                </button>
              )}
              {hasPermission('action:reject_reports') && (
                <button
                  onClick={() => setBulkRejectOpen(true)}
                  disabled={reject.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition-all active:translate-y-px disabled:opacity-50"
                  style={{
                    backgroundColor: ADMIN_COLORS.signal,
                    boxShadow: '0 1px 4px rgba(239,68,68,0.25)',
                  }}
                >
                  <X className="w-3.5 h-3.5" />
                  {t('reports.reject')} ({selectedReportObjects.length})
                </button>
              )}
            </>
          )}
          {hasPermission('action:export_data') && (
            <button
              onClick={() => setIsExportWizardOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all active:translate-y-px"
              style={{
                backgroundColor: 'var(--color-primary-500)',
                color: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              }}
            >
              <Download className="w-3.5 h-3.5" />
              {t('export.wizard.title')}
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex justify-center py-16"
          >
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: ADMIN_COLORS.textTertiary }} />
          </motion.div>
        ) : allReports.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-3 rounded-3xl py-16"
            style={{ border: `2px dashed ${ADMIN_COLORS.borderMedium}` }}
          >
            <ClipboardCheck className="h-10 w-10" style={{ color: ADMIN_COLORS.textTertiary }} />
            <p className="text-sm" style={{ color: ADMIN_COLORS.textSecondary }}>
              {t('admin.noPendingReports')}
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0 }}
          >
            <div ref={topBarRef} />

            {/* Payroll-style table */}
            <div className="overflow-x-auto rounded-3xl border border-white/30 glass-surface">
              <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '4%' }} />
                </colgroup>
                <thead>
                  <tr className="border-b-[2px] border-white/30" style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}>
                    <th className="ps-3 pe-2 py-2 text-start text-xs sm:text-sm font-semibold">
                      <div className="flex items-center gap-2">
                        <input
                          ref={(el) => {
                            if (el) {
                              const count = allReportIds.filter(id => selectedReports.has(id)).length;
                              el.indeterminate = count > 0 && count < allReportIds.length;
                            }
                          }}
                          type="checkbox"
                          checked={allReportIds.length > 0 && allReportIds.every(id => selectedReports.has(id))}
                          onChange={toggleSelectAll}
                          className="neo-checkbox h-3 w-3 shrink-0"
                        />
                        <span>{t('admin.employee')}</span>
                      </div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.statusCol')}</th>
                    <th className="px-2 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.reportsCol')}</th>
                    <th className="px-2 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.hospitals')}</th>
                    <th className="px-1 py-2 text-center text-xs sm:text-sm font-semibold">{t('quickReport.quantity')}</th>
                    <th className="px-2 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.subTotal')}</th>
                    <th className="px-2 py-2 text-end text-xs sm:text-sm font-semibold">{t('admin.totalCol')}</th>
                    <th className="ps-1 pe-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => {
                    const isExpanded = expandedIds.has(group.therapistId);
                    return (
                      <Fragment key={group.therapistId}>
                        <tr
                          className="border-b border-white/20 bg-white/25 hover:bg-white/35 cursor-pointer"
                          onClick={() => toggleExpand(group.therapistId)}
                        >
                          <td className="ps-3 pe-2 py-1.5 font-medium text-xs text-neutral-800">
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                ref={(el) => {
                                  if (el) el.indeterminate = isTherapistIndeterminate(group.therapistId);
                                }}
                                type="checkbox"
                                checked={isTherapistAllSelected(group.therapistId)}
                                onChange={() => toggleTherapist(group.therapistId)}
                                className="neo-checkbox h-3 w-3 shrink-0"
                              />
                              <span className="truncate" onClick={(e) => { e.stopPropagation(); toggleExpand(group.therapistId); }}>{group.therapistName}</span>
                            </div>
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            <GroupStatusBadge reports={group.reports} />
                          </td>
                          <td className="px-2 py-1.5 text-center text-xs text-neutral-600">
                            {group.totalReports}
                          </td>
                          <td className="px-2 py-1.5 text-center text-xs text-neutral-600">
                            {group.totalHospitals}
                          </td>
                          <td className="px-1 py-1.5 text-center text-xs text-neutral-600">
                            {group.totalQuantity}
                          </td>
                          <td className="px-2 py-1.5" />
                          <td className="px-2 py-1.5 text-end font-medium text-xs text-neutral-800">
                            {group.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {sym}
                          </td>
                          <td className="ps-1 pe-3 py-1.5">
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-slate-500" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-500" />
                            )}
                          </td>
                        </tr>
                        {isExpanded && (() => {
                          const allowances = allowancesByTherapist[group.therapistId] || [];
                          const svBonus = supervisionBonusesByTherapist[group.therapistId];
                          const svBreakdown = svBonus?.breakdown || [];
                          const svRowCount = svBreakdown.reduce((sum, sv) => sum + Math.max(sv.contributors?.length || 0, 1), 0);
                          const totalChildren = group.reports.length + allowances.length + svRowCount;
                          let childIdx = 0;
                          const lineColor = 'color-mix(in srgb, var(--color-primary-500) 30%, transparent)';
                          return (
                            <>
                              {group.reports.map((r) => {
                                const idx = childIdx++;
                                const isLast = idx === totalChildren - 1;
                                const typeName = r.reportTypeObj ? localizedName(r.reportTypeObj, i18n.language) : r.reportTypeCode || r.reportType;
                                return (
                                  <tr
                                    key={r.id}
                                    className="bg-white/8 text-xs border-b border-white/10 hover:bg-white/15 cursor-pointer"
                                    onClick={() => setParams({ report: r.id })}
                                  >
                                    <td className="ps-3 pe-2 py-1.5 text-neutral-500" style={{ position: 'relative' }}>
                                      {/* Tree connector */}
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: 0,
                                        bottom: isLast ? '50%' : 0,
                                        width: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: '50%',
                                        width: '0.5rem',
                                        height: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: 'calc(0.75rem + 0.5rem - 2px)',
                                        top: 'calc(50% - 2px)',
                                        width: '4px',
                                        height: '4px',
                                        borderRadius: '50%',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div className="flex items-center gap-1.5" style={{ marginInlineStart: '1.25rem' }} onClick={(e) => e.stopPropagation()}>
                                        <input
                                          type="checkbox"
                                          checked={selectedReports.has(r.id)}
                                          onChange={() => toggleReport(r.id)}
                                          className="neo-checkbox h-2.5 w-2.5 shrink-0"
                                        />
                                        <span className="truncate" onClick={() => setParams({ report: r.id })}>{r.date}</span>
                                      </div>
                                    </td>
                                    <td className="px-1 py-1.5 text-center">
                                      <StatusBadge status={r.status} />
                                    </td>
                                    <td className="px-2 py-1.5 text-center text-neutral-500 truncate">
                                      {typeName}
                                    </td>
                                    <td className="px-2 py-1.5 text-center text-neutral-500 truncate">
                                      {r.hospital ? localizedName(r.hospital, i18n.language) : ''}
                                    </td>
                                    <td className="px-1 py-1.5 text-center text-neutral-500">
                                      {r.quantity || 1}
                                    </td>
                                    <td className={`px-2 py-1.5 text-center text-neutral-500${r.status === 'REJECTED' ? ' line-through opacity-50' : ''}`}>
                                      {r.ratePerUnit > 0 ? (
                                        (r.hospitalBonusPerUnit ?? 0) > 0 ? (
                                          <>{(r.quantity || 1) > 1 ? `${r.quantity}×(` : ''}{r.ratePerUnit}+{r.hospitalBonusPerUnit}{(r.quantity || 1) > 1 ? ')' : ''}</>
                                        ) : (
                                          <>{(r.quantity || 1) > 1 ? `${r.quantity}×` : ''}{r.ratePerUnit}</>
                                        )
                                      ) : '-'}
                                    </td>
                                    <td className={`px-2 py-1.5 text-end font-medium text-neutral-700${r.status === 'REJECTED' ? ' line-through opacity-50' : ''}`}>
                                      {r.calculatedAmount != null ? `${r.calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${sym}` : '-'}
                                    </td>
                                    <td className="ps-1 pe-3 py-1.5" />
                                  </tr>
                                );
                              })}
                              {svBreakdown.flatMap((sv) => {
                                const contributors = sv.contributors || [];
                                const rows = contributors.length > 0 ? contributors : [null];
                                return rows.map((contrib, cIdx) => {
                                  const idx = childIdx++;
                                  const isLast = idx === totalChildren - 1;
                                  const contribAmount = contrib ? contrib.units * sv.bonusPerUnit : sv.amount;
                                  const contribUnits = contrib ? contrib.units : sv.totalUnits;
                                  return (
                                    <tr key={`sv-${sv.hospitalId}-${sv.reportTypeCode}-${contrib?.therapistId || cIdx}`} className="bg-amber-50/30 text-xs border-b border-white/15 hover:bg-amber-50/50 cursor-pointer" onClick={() => setSvDetail({ sv, contrib, therapistName: group.therapistName })}>
                                      <td className="ps-3 pe-2 py-1.5 text-amber-700 font-medium" style={{ position: 'relative' }}>
                                        {/* Tree connector */}
                                        <div style={{
                                          position: 'absolute',
                                          insetInlineStart: '0.75rem',
                                          top: 0,
                                          bottom: isLast ? '50%' : 0,
                                          width: '1px',
                                          backgroundColor: lineColor,
                                        }} />
                                        <div style={{
                                          position: 'absolute',
                                          insetInlineStart: '0.75rem',
                                          top: '50%',
                                          width: '0.5rem',
                                          height: '1px',
                                          backgroundColor: lineColor,
                                        }} />
                                        <div style={{
                                          position: 'absolute',
                                          insetInlineStart: 'calc(0.75rem + 0.5rem - 2px)',
                                          top: 'calc(50% - 2px)',
                                          width: '4px',
                                          height: '4px',
                                          borderRadius: '50%',
                                          backgroundColor: lineColor,
                                        }} />
                                        <span className="truncate" style={{ marginInlineStart: '1.25rem', display: 'block' }}>
                                          {contrib ? <span className="italic text-amber-600/80 inline-flex items-center gap-1"><Gift className="h-3 w-3 shrink-0" />{contrib.name}</span> : t('admin.supervisionBonus')}
                                        </span>
                                      </td>
                                      <td className="px-1 py-1.5 text-center">
                                        {(() => {
                                          const svStatus = deriveSvStatus(contrib?.reports || sv.contributors?.flatMap((c: any) => c.reports || []));
                                          const cfg = STATUS_BADGE_CONFIG[svStatus];
                                          if (!cfg) return null;
                                          const Icon = cfg.icon;
                                          return (
                                            <span
                                              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap"
                                              style={{ background: cfg.bg, color: cfg.fg, boxShadow: cfg.shadow }}
                                            >
                                              <Icon className="w-3 h-3" />
                                              {t(STATUS_LABEL_KEY[svStatus] ?? svStatus)}
                                              <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                                            </span>
                                          );
                                        })()}
                                      </td>
                                      <td className="px-2 py-1.5 text-center text-amber-700 truncate">
                                        {t('admin.supervisionBonus')}
                                      </td>
                                      <td className="px-2 py-1.5 text-center text-amber-700 font-medium truncate">
                                        {i18n.language === 'he' ? sv.hospitalName : (sv.hospitalNameEn || sv.hospitalName)}
                                      </td>
                                      <td className="px-1 py-1.5 text-center text-amber-700">{contribUnits}</td>
                                      <td className="px-2 py-1.5 text-center text-amber-700">
                                        {contribUnits}×{sv.bonusPerUnit}
                                      </td>
                                      <td className="px-2 py-1.5 text-end font-medium text-amber-800">
                                        {contribAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {sym}
                                      </td>
                                      <td className="ps-1 pe-3 py-1.5" />
                                    </tr>
                                  );
                                });
                              })}
                              {allowances.map((a) => {
                                const idx = childIdx++;
                                const isLast = idx === totalChildren - 1;
                                return (
                                  <tr key={`allowance-${a.id}`} className="bg-primary-50/30 text-xs border-b border-white/15">
                                    <td className="ps-3 pe-2 py-1.5 text-primary-600 font-medium" style={{ position: 'relative' }}>
                                      {/* Tree connector */}
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: 0,
                                        bottom: isLast ? '50%' : 0,
                                        width: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: '50%',
                                        width: '0.5rem',
                                        height: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: 'calc(0.75rem + 0.5rem - 2px)',
                                        top: 'calc(50% - 2px)',
                                        width: '4px',
                                        height: '4px',
                                        borderRadius: '50%',
                                        backgroundColor: lineColor,
                                      }} />
                                      <span className="truncate inline-flex items-center gap-1" style={{ marginInlineStart: '1.25rem' }}>
                                        {a.code === 'TRAVEL' && <Car className="h-3 w-3 shrink-0" />}
                                        {t('admin.allowance')}
                                      </span>
                                    </td>
                                    <td className="px-1 py-1.5" />
                                    <td className="px-2 py-1.5" />
                                    <td className="px-2 py-1.5 text-center text-primary-600 font-medium truncate">
                                      {i18n.language === 'he' ? a.name : (a.nameEn || a.name)}
                                    </td>
                                    <td className="px-1 py-1.5 text-center text-primary-600">1</td>
                                    <td className="px-2 py-1.5 text-center text-primary-600">
                                      1×{a.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-2 py-1.5 text-end font-medium text-primary-700">
                                      {a.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {sym}
                                    </td>
                                    <td className="ps-1 pe-3 py-1.5" />
                                  </tr>
                                );
                              })}
                            </>
                          );
                        })()}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-[2px] border-white/30 text-xs sm:text-sm font-semibold" style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}>
                    <td className="pe-2 py-2" style={{ paddingInlineStart: 'calc(0.75rem + 12px + 8px)' }}>{t('admin.total')} ({totalEmployees})</td>
                    <td className="px-1 py-2" />
                    <td className="px-2 py-2 text-center">{totalPending}</td>
                    <td className="px-2 py-2 text-center">{groups.reduce((sum, g) => sum + g.totalHospitals, 0)}</td>
                    <td className="px-1 py-2 text-center">{groups.reduce((sum, g) => sum + g.totalQuantity, 0)}</td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-end font-bold">
                      {Math.round(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {sym}
                    </td>
                    <td className="ps-1 pe-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>


            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-4" />
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin" style={{ color: ADMIN_COLORS.textTertiary }} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

{/* Removed FAB — approve bar is inline above/below table */}

      {/* Report Detail Modal */}
      <FormModal
        isOpen={!!detailReport}
        onClose={closeDetailModal}
        title={detailReport ? (detailReport.reportTypeObj ? localizedName(detailReport.reportTypeObj, i18n.language) : detailReport.reportType) : ''}
        maxWidth="max-w-md"
      >
        {detailReport && (
          <div className="space-y-2">
            {/* Employee & Hospital */}
            <div className="rounded-xl p-3 space-y-1.5" style={detailCardStyle}>
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-3.5 h-3.5 opacity-50" />
                <span className="font-bold">{t('admin.employee')}:</span>
                <span className="opacity-70">{detailReport.therapist.firstName} {detailReport.therapist.lastName}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-3.5 h-3.5 opacity-50" />
                <span className="font-bold">{t('reports.hospital')}:</span>
                <span className="opacity-70">{localizedName(detailReport.hospital, i18n.language)} ({detailReport.hospital.code})</span>
              </div>
            </div>

            {/* Date & Type */}
            <div className="rounded-xl p-3 space-y-1.5" style={detailCardStyle}>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-3.5 h-3.5 opacity-50" />
                <span className="font-bold">{t('reports.date')}:</span>
                <span className="opacity-70">{detailReport.date}</span>
              </div>
              {detailReport.treatmentType && (
                <div className="flex items-center gap-2 text-sm">
                  <ClipboardCheck className="w-3.5 h-3.5 opacity-50" />
                  <span className="font-bold">{t('reports.treatmentType')}:</span>
                  <span className="opacity-70">{t(`treatments.${detailReport.treatmentType}`, detailReport.treatmentType)}</span>
                </div>
              )}
            </div>

            {/* Calculation */}
            <div className="rounded-xl p-3 space-y-1.5" style={detailCardStyle}>
              <div className="flex items-center gap-2 text-sm">
                <ClipboardCheck className="w-3.5 h-3.5 opacity-50" />
                <span className="font-bold">{t('quickReport.quantity')}:</span>
                <span className="opacity-70">×{detailReport.quantity || 1}</span>
              </div>
              {detailReport.ratePerUnit > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Banknote className="w-3.5 h-3.5 opacity-50" />
                  <span className="font-bold">{t('admin.ratePerUnit')}:</span>
                  <span className="opacity-70 inline-flex items-center gap-2">
                    {detailReport.ratePerUnit} {sym}
                    {(detailReport.hospitalBonusPerUnit ?? 0) > 0 && (
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={{ backgroundColor: '#d1fae5', color: '#065f46' }}
                      >
                        +{detailReport.hospitalBonusPerUnit} {t('employmentRoles.hospitalBonuses.title')}
                      </span>
                    )}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm pt-1 border-t" style={{ borderColor: MODAL_COLORS.border }}>
                <Banknote className="w-3.5 h-3.5" style={{ color: ADMIN_COLORS.primaryText }} />
                <span className="font-bold">{t('admin.totalCol')}:</span>
                <span className="font-bold" style={{ color: ADMIN_COLORS.primaryText }}>
                  {(detailReport.calculatedAmount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {sym}
                </span>
              </div>
            </div>

            {/* Notes */}
            {detailReport.description && (
              <div className="rounded-xl p-3" style={detailCardStyle}>
                <div className="flex items-start gap-2 text-sm">
                  <ClipboardCheck className="w-3.5 h-3.5 opacity-50 mt-0.5" />
                  <span className="font-bold">{t('reports.notes')}:</span>
                  <span className="opacity-70">{detailReport.description}</span>
                </div>
              </div>
            )}

            {/* Action buttons */}
            {detailReport.status !== 'PAID' && (
              <div className="flex gap-3 pt-2">
                {hasPermission('action:approve_reports') && detailReport.status !== 'APPROVED' && (
                  <button
                    onClick={() => handleApprove(detailReport)}
                    disabled={approve.isPending}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-all active:translate-y-px disabled:opacity-50"
                    style={{
                      backgroundColor: ADMIN_COLORS.success,
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(10,149,72,0.3)',
                    }}
                  >
                    <Check className="h-4 w-4" />
                    {t('reports.approve')}
                  </button>
                )}
                {hasPermission('action:approve_reports') && detailReport.status !== 'SUBMITTED' && (
                  <button
                    onClick={() => handleRePending(detailReport)}
                    disabled={rePending.isPending}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-all active:translate-y-px disabled:opacity-50"
                    style={{
                      backgroundColor: '#0891b2',
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(8,145,178,0.3)',
                    }}
                  >
                    <Clock className="h-4 w-4" />
                    {t('reports.rePending')}
                  </button>
                )}
                {hasPermission('action:reject_reports') && detailReport.status !== 'REJECTED' && (
                  <button
                    onClick={() => setRejectingId(detailReport.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-all active:translate-y-px"
                    style={{
                      backgroundColor: ADMIN_COLORS.signal,
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                    }}
                  >
                    <X className="h-4 w-4" />
                    {t('reports.reject')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </FormModal>

      {/* Rejection reason modal (single report) */}
      {rejectingId && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
            onClick={() => setRejectingId(null)}
          />
          <div
            className="fixed z-[9999] flex flex-col items-center gap-4 rounded-3xl px-6 py-6"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadowLg,
              minWidth: '300px',
              maxWidth: '400px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold" style={{ color: ADMIN_COLORS.navy }}>
              {t('reports.rejectionReason')}
            </h3>
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && rejectReason.trim()) {
                  const report = allReports.find(r => r.id === rejectingId);
                  if (report) handleReject(report);
                }
              }}
              placeholder={t('reports.rejectionReason')}
              autoFocus
              className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{
                border: `1px solid ${ADMIN_COLORS.borderMedium}`,
                fontSize: '16px',
                color: ADMIN_COLORS.navy,
              }}
            />
            <div className="flex gap-3 w-full">
              <button
                onClick={() => {
                  const report = allReports.find(r => r.id === rejectingId);
                  if (report) handleReject(report);
                }}
                disabled={!rejectReason.trim() || reject.isPending}
                className="flex-1 py-3 rounded-lg font-semibold text-sm text-white disabled:opacity-40 transition-colors"
                style={{
                  backgroundColor: ADMIN_COLORS.signal,
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                }}
              >
                {t('reports.reject')}
              </button>
              <button
                onClick={() => { setRejectingId(null); setRejectReason(''); }}
                className="flex-1 py-3 rounded-lg font-semibold text-sm transition-colors"
                style={{
                  border: `1px solid ${ADMIN_COLORS.borderMedium}`,
                  color: ADMIN_COLORS.textSecondary,
                  backgroundColor: ADMIN_COLORS.surface,
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}

      {/* Bulk rejection reason modal */}
      {bulkRejectOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm"
            onClick={() => { setBulkRejectOpen(false); setBulkRejectReason(''); }}
          />
          <div
            className="fixed z-[9999] flex flex-col items-center gap-4 rounded-3xl px-6 py-6"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadowLg,
              minWidth: '300px',
              maxWidth: '400px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold" style={{ color: ADMIN_COLORS.navy }}>
              {t('reports.rejectionReason')}
            </h3>
            <p className="text-xs text-center" style={{ color: ADMIN_COLORS.textSecondary }}>
              {selectedReportObjects.length} {t('admin.reportsCol')}
            </p>
            <input
              type="text"
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && bulkRejectReason.trim()) handleBulkReject(); }}
              placeholder={t('reports.rejectionReason')}
              autoFocus
              className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{
                border: `1px solid ${ADMIN_COLORS.borderMedium}`,
                fontSize: '16px',
                color: ADMIN_COLORS.navy,
              }}
            />
            <div className="flex gap-3 w-full">
              <button
                onClick={handleBulkReject}
                disabled={!bulkRejectReason.trim() || bulkReject.isPending}
                className="flex-1 py-3 rounded-lg font-semibold text-sm text-white disabled:opacity-40 transition-colors"
                style={{
                  backgroundColor: ADMIN_COLORS.signal,
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                }}
              >
                {bulkReject.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  t('reports.reject')
                )}
              </button>
              <button
                onClick={() => { setBulkRejectOpen(false); setBulkRejectReason(''); }}
                className="flex-1 py-3 rounded-lg font-semibold text-sm transition-colors"
                style={{
                  border: `1px solid ${ADMIN_COLORS.borderMedium}`,
                  color: ADMIN_COLORS.textSecondary,
                  backgroundColor: ADMIN_COLORS.surface,
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
      <ExportWizardModal
        isOpen={isExportWizardOpen}
        onClose={() => setIsExportWizardOpen(false)}
        employees={wizardEmployees}
        statuses={wizardStatuses}
        onExport={handleWizardExport}
        isPending={wizardExporting}
      />

      {/* Supervision Bonus Detail Modal */}
      <FormModal
        isOpen={!!svDetail}
        onClose={() => setSvDetail(null)}
        title={t('admin.supervisionBonus')}
        maxWidth="max-w-sm"
      >
        {svDetail && (() => {
          const { sv, contrib, therapistName } = svDetail;
          const contribUnits = contrib ? contrib.units : sv.totalUnits;
          const contribAmount = contrib ? contrib.units * sv.bonusPerUnit : sv.amount;
          const hospitalName = i18n.language === 'he' ? sv.hospitalName : (sv.hospitalNameEn || sv.hospitalName);
          const svCardStyle = { backgroundColor: MODAL_COLORS.interactive, border: `1px solid ${MODAL_COLORS.border}` };
          return (
            <div className="space-y-2">
              {/* Supervisor */}
              <div className="rounded-xl p-3" style={svCardStyle}>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-3.5 h-3.5 opacity-50" />
                  <span className="font-bold">{t('admin.supervisor')}:</span>
                  <span className="opacity-70">{therapistName}</span>
                </div>
              </div>

              {/* Details */}
              <div className="rounded-xl p-3 space-y-1.5" style={svCardStyle}>
                {contrib && (
                  <div className="flex items-center gap-2 text-sm">
                    <Gift className="w-3.5 h-3.5 text-amber-500" />
                    <span className="font-bold">{t('admin.contributor')}:</span>
                    <span className="opacity-70 italic">{contrib.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-3.5 h-3.5 opacity-50" />
                  <span className="font-bold">{t('admin.hospitals')}:</span>
                  <span className="opacity-70">{hospitalName} ({sv.hospitalCode})</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <ClipboardCheck className="w-3.5 h-3.5 opacity-50" />
                  <span className="font-bold">{t('admin.reportsCol')}:</span>
                  <span className="opacity-70">{sv.reportTypeCode}</span>
                </div>
              </div>

              {/* Calculation */}
              <div className="rounded-xl p-3 space-y-1.5" style={svCardStyle}>
                <div className="flex items-center gap-2 text-sm">
                  <Banknote className="w-3.5 h-3.5 opacity-50" />
                  <span className="font-bold">{t('admin.bonusPerUnit')}:</span>
                  <span className="opacity-70">{sv.bonusPerUnit} {sym}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <ClipboardCheck className="w-3.5 h-3.5 opacity-50" />
                  <span className="font-bold">{t('admin.quantity')}:</span>
                  <span className="opacity-70">{contribUnits}</span>
                </div>
                <div className="flex items-center gap-2 text-sm pt-1 border-t" style={{ borderColor: MODAL_COLORS.border }}>
                  <Banknote className="w-3.5 h-3.5 text-amber-600" />
                  <span className="font-bold">{t('admin.totalCol')}:</span>
                  <span className="font-bold text-amber-700">
                    {contribAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {sym}
                  </span>
                </div>
              </div>

              {/* Contributing Reports */}
              {(() => {
                const reports = contrib?.reports || sv.contributors?.flatMap((c: any) => (c.reports || []).map((r: any) => ({ ...r, contributorName: c.name }))) || [];
                if (reports.length === 0) return null;
                return (
                  <div className="rounded-xl p-3" style={svCardStyle}>
                    <p className="text-xs font-bold opacity-60 uppercase mb-2">{t('admin.contributingReports')}</p>
                    <div className="space-y-1">
                      {reports.map((r: any) => (
                        <div
                          key={r.id}
                          className="flex items-center justify-between text-sm px-2 py-1.5 rounded-lg hover:bg-amber-50 cursor-pointer transition-colors"
                          onClick={() => { setSvDetail(null); setParams({ report: r.id }); }}
                        >
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3 opacity-40" />
                            <span className="opacity-80">{r.date}</span>
                            {!contrib && r.contributorName && (
                              <span className="text-xs italic text-amber-600/70">— {r.contributorName}</span>
                            )}
                          </div>
                          <StatusBadge status={r.status} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* All contributors */}
              {sv.contributors && sv.contributors.length > 1 && (
                <div className="rounded-xl p-3" style={svCardStyle}>
                  <p className="text-xs font-bold opacity-60 uppercase mb-2">{t('admin.allContributors')}</p>
                  <div className="space-y-1">
                    {sv.contributors.map((c: any) => (
                      <div
                        key={c.therapistId}
                        className={`flex items-center justify-between text-sm px-2 py-1.5 rounded-lg ${contrib?.therapistId === c.therapistId ? 'ring-1 ring-amber-200' : ''}`}
                        style={{ backgroundColor: contrib?.therapistId === c.therapistId ? 'rgba(251,191,36,0.1)' : 'transparent' }}
                      >
                        <div className="flex items-center gap-1.5">
                          <Gift className="w-3 h-3 text-amber-500 opacity-60" />
                          <span className={contrib?.therapistId === c.therapistId ? 'font-semibold text-amber-700' : 'opacity-70'}>
                            {c.name}
                          </span>
                        </div>
                        <span className="opacity-60 text-xs">
                          {c.units} × {sv.bonusPerUnit} = <span className="font-semibold opacity-100">{(c.units * sv.bonusPerUnit).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {sym}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </FormModal>
    </motion.div>
  );
}
